import MenuItem from './MenuItem';

interface IProduct {
  items: MenuItem[];
  defaultServings: number;

  readonly productId?: number;
  readonly lowestServingItem?: MenuItem;
  readonly highestServingItem?: MenuItem;
  readonly servingOptions?: number[];
  readonly hasServingOptions?: boolean;
}

/**
 * A Product is a group of different MenuItems with different SKU ids that
 * are conceptually the same product, but have different serving options.
 * They may show as different items in the API, but are shown grouped when
 * they are presented, usually with a UI to select servings.
 *
 * Right now we only support two serving options, but could be extended if
 * required in the future.
 * */
abstract class Product<Item extends MenuItem> implements IProduct {
  items: Item[];
  defaultServings: number;

  constructor(defaultServings: number, ...items: Item[]) {
    this.defaultServings = defaultServings;
    this.items = items;
    this.sort();
  }

  add(item: Item): void {
    this.items.push(item);
    this.sort();
  }

  sort(): void {
    if (this.items.length > 1) {
      // @ts-expect-error TODO: fix null safety
      this.items.sort((a, b) => a.sku.servings - b.sku.servings);
    }
  }

  get productId(): number {
    return this.items[0] ? this.items[0].sku.product_id : 0;
  }

  get lowestServingItem(): Item {
    return this.items[0];
  }

  get highestServingItem(): Item {
    return this.items[this.items.length - 1];
  }

  get servingOptions(): number[] {
    // @ts-expect-error TODO: fix null safety
    return this.items.map((item) => item.sku.servings);
  }

  /**
   * Returns true if this product has multiple SKUs in it's items array,
   * which means that it has different serving options to choose from.
   */
  get hasServingOptions(): boolean {
    return this.items.length > 1;
  }

  hasSku(skuId: number): boolean {
    return this.items.some((item) => item.sku.id === skuId);
  }

  hasProductId(productId: number): boolean {
    return this.items.some((item) => item.sku.product_id === productId);
  }

  equals(product: IProduct): boolean {
    return this === product
      || this.items.every((item, index) => (
        product.items[index].sku.id === item.sku.id
      ));
  }
}

export type { IProduct };
export default Product;
